import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import VideoCard from '@/components/base/VideoCard';
import ArticleCard from '@/components/base/ArticleCard';
import SectionHeader from '@/components/base/SectionHeader';
import { videos, videoCategories, featuredVideos, latestVideos, popularVideos } from '@/mocks/videos';
import { articles as allArticles } from '@/mocks/articles';

export default function Home() {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredVideos = useMemo(() => {
    let result = videos;
    if (activeCategory !== 'all') {
      result = result.filter(v => v.categorySlug === activeCategory);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        v =>
          v.title.toLowerCase().includes(q) ||
          v.tags.some(t => t.toLowerCase().includes(q)) ||
          v.description.toLowerCase().includes(q)
      );
    }
    return result;
  }, [activeCategory, searchQuery]);

  const recentArticles = allArticles.slice(0, 3);

  // Organization Schema
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '密桃视频',
    url: window.location.origin,
    description: '密桃视频提供香蕉视频、糖豆视频等热门平台高清影视资源，全集免费在线观看不缓冲',
    sameAs: [
      'https://www.xjsp.com',
      'https://www.tdsp.com',
    ],
  };

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '密桃视频',
    url: window.location.origin,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${window.location.origin}/?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webSiteSchema)}</script>

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[550px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20home%20cinema%20living%20room%20with%20large%20TV%20screen%20displaying%20colorful%20movie%20posters%2C%20warm%20ambient%20lighting%2C%20cozy%20atmosphere%2C%20dark%20accent%20walls%2C%20professional%20interior%20design%2C%20entertainment%20focused%20space%2C%20clean%20minimalist%20style&width=1920&height=800&seq=mitao-hero-01&orientation=landscape"
          alt="密桃视频 - 高清影视在线观看"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60" />
        <div className="relative z-10 w-full max-w-4xl mx-auto text-center px-4">
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold font-heading text-white mb-4 leading-tight">
            密桃视频 — 高清全集免费在线观看
          </h1>
          <p className="text-sm md:text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            聚合香蕉视频、糖豆视频、小蓝视频、蓝莓视频热门资源，畅享不缓冲观影体验
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {videoCategories.slice(1, 5).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-5 py-2.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/25 transition-all duration-300 border border-white/20 whitespace-nowrap cursor-pointer"
              >
                <i className={`${cat.icon} mr-1.5 w-4 h-4 inline-flex items-center justify-center align-middle`} />
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Category Navigation */}
        <section className="py-8 md:py-10">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {videoCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveCategory(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`} />
                {cat.name}
              </button>
            ))}
          </div>

          {searchQuery && (
            <p className="mt-4 text-sm text-foreground-500">
              搜索 &ldquo;{searchQuery}&rdquo; 的结果，共 {filteredVideos.length} 部影片
            </p>
          )}
        </section>

        {/* Featured Section */}
        <section className="pb-10 md:pb-14">
          <SectionHeader
            title="精选推荐"
            subtitle="编辑团队每日精选优质内容"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {(activeCategory === 'all' && !searchQuery ? featuredVideos : filteredVideos.slice(0, 6)).map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        {/* Popular / Category Results */}
        <section className="pb-10 md:pb-14">
          <SectionHeader
            title={activeCategory === 'all' && !searchQuery ? '热门排行' : '分类浏览'}
            subtitle={activeCategory === 'all' && !searchQuery ? '根据播放量排序' : undefined}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {(activeCategory === 'all' && !searchQuery
              ? popularVideos.slice(0, 10)
              : filteredVideos.slice(6, 16)
            ).map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        {/* Latest Updates */}
        <section className="pb-10 md:pb-14">
          <SectionHeader
            title="最新更新"
            subtitle="每日更新，追剧不等待"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {(activeCategory === 'all' && !searchQuery
              ? latestVideos.slice(0, 10)
              : filteredVideos.slice(16, 26)
            ).map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        </section>

        {/* Articles Section */}
        <section className="pb-10 md:pb-14">
          <SectionHeader
            title="影视资讯"
            subtitle="最新影视动态与深度解读"
          />
          <div className="space-y-4">
            {recentArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </section>

        {/* Platform Partners */}
        <section className="pb-10 md:pb-14">
          <SectionHeader title="合作平台" subtitle="多平台资源聚合" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { name: '香蕉视频', desc: '热门影视资源库', icon: 'ri-movie-line' },
              { name: '糖豆视频', desc: '海量高清影片', icon: 'ri-film-line' },
              { name: '小蓝视频', desc: '最新电视剧大全', icon: 'ri-tv-line' },
              { name: '蓝莓视频', desc: '免费电影不缓冲', icon: 'ri-play-circle-line' },
            ].map((platform) => (
              <div
                key={platform.name}
                className="flex flex-col items-center justify-center p-6 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-300/60 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className={`${platform.icon} text-xl w-6 h-6 flex items-center justify-center`} />
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{platform.name}</h4>
                <p className="text-xs text-foreground-500 mt-1">{platform.desc}</p>
              </div>
            ))}
          </div>
        </section>

      </div>
    </>
  );
}